// Imports
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import Input from '../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Select from '../../../../../shareables/foundation/front-end/components/forms/fields/select';
import commonActions from '../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../shareables/foundation/front-end/redux/hooks';
import selfActions from '../../../../../redux/self/actions';
import APIForm, { APIFormOptions } from '../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../shareables/integrations/api/redux/actions';
import { GetSurveyResponse } from '../../../../../shareables/types/api/main/website/survey/get';
import { PostResponseParameters } from '../../../../../shareables/types/api/main/website/survey/response/post';
import parseAndSanitizeMarkdown from '../../../../../shareables/utils/markdown/parse-and-sanitize';
import { GeneratedThemeProps } from '../../../../templates/template-style';
import FormSubmitted from '../page-types/shared-components/form-submitted';
import PageTitle from '../shared-components/page-title';
import LeadingText from '../shared-components/leading-text';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;


// Define the accepted props
interface Props {
	storybookSurvey?: GetSurveyResponse;
	storybookSubmitted?: true;
	storybookErrorMessage?: string;
}


// Function component
const TakeSurvey: React.FC<Props> = ({ storybookSurvey, storybookSubmitted, storybookErrorMessage }) => {
	// Use state
	const [survey, setSurvey] = useState<GetSurveyResponse | null>(storybookSurvey ?? null);
	const [formSubmitted, setFormSubmitted] = useState(storybookSubmitted ?? false);
	const [errorMessage, setErrorMessage] = useState(storybookErrorMessage ?? '');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	
	
	// Use React Router functionality
	const [searchParams] = useSearchParams();
	const surveyID = searchParams.get('id');
	
	
	// Load survey on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetSurveyResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/survey/${surveyID ?? ''}`,
				handleErrorsAutomatically: false,
				completion: (json) => {
					if (json.status === 'error') {
						// Handle expected error messages
						if (json.message.toLowerCase().includes('member has already taken survey.')) {
							setErrorMessage('It appears you’ve already taken this survey. Thanks!');
							return;
						}
						
						if (json.message.toLowerCase().includes('account has already taken survey.')) {
							setErrorMessage('It appears someone else on your account already took this survey. Thanks!');
							return;
						}
						
						
						// Handle all other errors
						dispatch(
							commonActions.showPageMessage({
								color: 'danger',
								title: 'Oh snap!',
								message: json.message,
							})
						);
					} else {
						setSurvey(json.response);
					}
				},
			})
		);
	}, [dispatch, surveyID]);
	
	
	// If loading, then display nothing
	if (survey === null && !errorMessage) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PostResponseParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/survey/${surveyID ?? ''}/response`,
		submitButton: {
			text: 'Submit',
		},
		extraParameters: {
			updateToken: survey?.updateToken,
		},
	};
	
	
	// Handle completion
	const handleCompletion = () => {
		// Update state
		setFormSubmitted(true);
		
		
		// Update Redux so notification badge shows correct count, optimistically
		if (member?.notifications) {
			dispatch(
				selfActions.storeMember({
					...member,
					notifications: {
						...member.notifications,
						availableSurveys: Math.max(0, member.notifications.availableSurveys - 1),
					},
				})
			);
		}
	};
	
	
	// Return JSX
	return (
		<Container>
			<PageTitle>Take survey</PageTitle>
			{survey?.introduction && <LeadingText>{parseAndSanitizeMarkdown(survey.introduction)}</LeadingText>}
			
			{errorMessage}
			
			{survey &&
				(formSubmitted ? (
					<FormSubmitted header='Survey submitted!' body='We’ve received your response. Thank you for your time.' />
				) : (
					<APIForm options={formOptions} onCompletion={handleCompletion}>
						{survey.questions.map((question, questionIndex) => {
							const baseInputProps = {
								key: questionIndex,
								label: question.prompt,
								name: `answers[${questionIndex}]`,
								required: true,
							};
							
							return question.type === 'FREE_SHORT' ? (
								<Input {...baseInputProps} type='text' />
							) : (
								<Select {...baseInputProps}>
									{question.answers.map((answer, answerIndex) => (
										<option value={answer} key={answerIndex}>
											{answer}
										</option>
									))}
								</Select>
							);
						})}
					</APIForm>
				))}
		</Container>
	);
};

export default TakeSurvey;
